import {useEffect, useState, useMemo} from 'react';
import {useQuery, useInfiniteQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {fetchPointsHistory, fetchRedemptionHistory, claimRedemption} from '../endpoints/api';
import {union, orderBy, uniqBy} from 'lodash';
import useAuth from '../hooks/useAuth';

export const useGetRedemptionHistory = (page: number, isClaimed: boolean = true) => {
  return useQuery(['redemption-history', page, isClaimed], () =>
    fetchRedemptionHistory({page: page, cancelReq: null, ...{perPage: 4, is_claimed: isClaimed}})
  );
};

export const useInfiniteRedemptionHistory = (isClaimed: boolean = true) => {
  return useInfiniteQuery(
    ['infinite-redemption-history', isClaimed],
    ({pageParam = 1}) => fetchRedemptionHistory({page: pageParam, cancelReq: null, per_page: 10, is_claimed: isClaimed}),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.data.current_page !== lastPage.data.last_page) {
          return lastPage.data.current_page + 1;
        }
      },
    }
  );
};

export const useGetPointHistory = (page: number) => {
  return useQuery(['point-history', page], () => fetchPointsHistory({page: page.toString(), perPage: 4}));
};

export const useRedemptionHistory = ({isClaimed = true}) => {
  const infiniteQuery = useInfiniteRedemptionHistory(isClaimed);
  
  // Flatten all pages into a single array
  const redemptionHistory = useMemo(() => {
    if (!infiniteQuery.data?.pages) return [];
    
    return infiniteQuery.data.pages.flatMap(page => page.data.data || []);
  }, [infiniteQuery.data?.pages]);

  const hasMore = useMemo(() => {
    if (!infiniteQuery.data?.pages?.length) return false;
    const lastPage = infiniteQuery.data.pages[infiniteQuery.data.pages.length - 1];
    return lastPage.data.current_page < lastPage.data.last_page;
  }, [infiniteQuery.data?.pages]);

  return {
    redemptionHistory,
    hasMore,
    isLoading: infiniteQuery.isLoading,
    fetchNextPage: infiniteQuery.fetchNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
  };
};

export const usePointHistory = () => {
  const [totalHistory, setTotalHistory] = useState<Array<any>>([]);
  const [pointHistory, setPointHistory] = useState<Array<any>>([]);
  const [redemptionHistory, setRedemptionHistory] = useState<Array<any>>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const getPointHistory = useGetPointHistory(page);
  const getRedemptionHistory = useGetRedemptionHistory(page);
  const {reFetchUserData} = useAuth();

  const isLoading = getPointHistory.isLoading || getRedemptionHistory.isLoading;

  useEffect(() => {
    if (getPointHistory.data?.data.data) {
      setPointHistory((prevHistory) =>
        uniqBy(
          orderBy(union(prevHistory, getPointHistory.data?.data.data), 'created_at', 'desc'),
          (e) => e.id
        )
      );
    }
  }, [getPointHistory.data?.data.data]);

  useEffect(() => {
    if (getRedemptionHistory.data?.data?.data) {
      setRedemptionHistory((prevHistory) =>
        uniqBy(
          orderBy(union(prevHistory, getRedemptionHistory.data?.data.data), 'created_at', 'desc'),
          (e) => e.id
        )
      );
    }
  }, [getRedemptionHistory.data?.data?.data.length]);

  useEffect(() => {
    setTotalHistory(orderBy(union(pointHistory, redemptionHistory), 'created_at', 'desc'));
    setHasMore(
      getRedemptionHistory.data?.data.last_page > getRedemptionHistory.data?.data.current_page ||
        getPointHistory.data?.data.last_page > getPointHistory.data?.data.current_page
    );
    reFetchUserData();
  }, [pointHistory, redemptionHistory]);

  return {setPage, isLoading, hasMore, totalHistory};
};

export const useClaimRedemption = () => {
  const queryClient = useQueryClient();

  return useMutation(({redemptionId}: {redemptionId: number}) => claimRedemption({redemptionId}), {
    onMutate: async ({redemptionId}) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries(['infinite-redemption-history']);
      await queryClient.cancelQueries(['redemption-history']);

      // Snapshot the previous values for both query types
      const previousInfiniteData = queryClient.getQueriesData(['infinite-redemption-history']);
      const previousData = queryClient.getQueriesData(['redemption-history']);

      // Optimistically update infinite query cache
      queryClient.setQueriesData(['infinite-redemption-history'], (old: any) => {
        if (!old?.pages) return old;

        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            data: {
              ...page.data,
              data: page.data.data.filter((redemption: any) => redemption.id !== redemptionId),
            },
          })),
        };
      });

      // Optimistically update regular redemption-history queries
      queryClient.setQueriesData(['redemption-history'], (old: any) => {
        if (!old?.data?.data) return old;

        const updatedData = old.data.data.filter(
          (redemption: any) => redemption.id !== redemptionId
        );

        return {
          ...old,
          data: {
            ...old.data,
            data: updatedData,
          },
        };
      });

      // Return context with the previous data for rollback on error
      return {previousInfiniteData, previousData};
    },
    onError: (_err, _redemptionId, context) => {
      // Rollback to the previous data on error
      if (context?.previousInfiniteData) {
        context.previousInfiniteData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we're in sync with the server
      queryClient.invalidateQueries(['infinite-redemption-history']);
      queryClient.invalidateQueries(['redemption-history']);
    },
  });
};
